"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const debug_1 = __importDefault(require("debug"));
const rxjs_1 = require("rxjs");
const operators_1 = require("rxjs/operators");
const result_1 = require("../utils/result");
const cancel_token_1 = require("../utils/cancel-token");
function commandError(code, originalError) {
    return {
        code,
        originalError
    };
}
function canceledCommandError() {
    return { code: 'canceled' };
}
const debug = debug_1.default('error-handling-queue');
debug.enabled = true;
class ErrorHandlingQueue {
    constructor(commandExecutor, errorHandler, errorReporting, debugName) {
        this.commandExecutor = commandExecutor;
        this.errorHandler = errorHandler;
        this.errorReporting = errorReporting;
        this.debugName = debugName;
        this.currentCommand = null;
        this.notificationSubject = new rxjs_1.Subject();
        this.cancelToken = cancel_token_1.cancelToken();
        this.phase = 'none';
        this.currentQueueSize = 0;
    }
    get notifications() {
        return this.notificationSubject;
    }
    start(retryStrategy) {
        if (this.phase !== 'none') {
            throw new Error('Coding error: already started error handling queue');
        }
        this.retryStrategy = retryStrategy;
        debug('starting');
        cancel_token_1.reset(this.cancelToken);
        this.commandSubject = new rxjs_1.Subject();
        this.observable = this.commandSubject.pipe(operators_1.observeOn(rxjs_1.queueScheduler), operators_1.concatMap(command => this.runCommand(command)));
        // used for waiting the queue to finish
        this.queueFinishedPromise = new Promise((resolve) => {
            this.queueFinishedResolve = resolve;
        });
        this.subscription = this.observable.subscribe({
            error: (err) => {
                this.errorReporting.sendFatalException(err, `Unrecoverable error while executing command in '${this.debugName}'.`);
                debug('subscription error', err);
                this.gotoFatalPhase();
                throw err;
            },
            complete: () => {
                if (!this.queueFinishedResolve) {
                    throw new Error('coding error: queueFinishedResolve not set');
                }
                debug('subscription complete');
                this.queueFinishedResolve();
            }
        });
        debug('started');
        this.phase = 'started';
    }
    async runCommand(command) {
        this.currentQueueSize--;
        debug(`${this.debugName}: running command, queue size: ${this.currentQueueSize}`);
        try {
            const job = command.delegate(command.cancelToken);
            this.currentCommand = job;
            return await job;
        }
        finally {
            this.currentCommand = null;
        }
    }
    async finish(options) {
        debug('finish', options, this.phase);
        if (this.phase === 'stopping' || this.phase === 'stopped') {
            debug('already stopped, return');
            return;
        }
        if (this.phase === 'none') {
            this.errorReporting.sendNonFatalError('Error handling queue is not started');
            return;
        }
        if (!this.subscription || !this.commandSubject) {
            throw new Error('Coding error: start has not called');
        }
        this.phase = 'stopping';
        debug('stopping');
        if (options.cancel) {
            cancel_token_1.cancel(this.cancelToken);
        }
        this.commandSubject.complete();
        await this.queueFinishedPromise;
        debug('queue stopped');
        this.subscription.unsubscribe();
        debug('queue unsubcribed');
        const lastNotification = this.notificationSubject.pipe(operators_1.last(undefined, null));
        if (this.currentCommand) {
            await this.currentCommand;
        }
        this.notificationSubject.complete();
        await lastNotification.toPromise();
        debug('stopped');
        this.phase = 'stopped';
        this.commandSubject = undefined;
        this.subscription = undefined;
        this.observable = undefined;
    }
    gotoFatalPhase() {
        if (this.queueFinishedResolve) {
            this.queueFinishedResolve();
        }
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }
    queue(command, cancelToken) {
        this.queueCommand(command, {
            delegate: (cancel) => this.handleCommand(command, cancel),
            cancelToken: this.commandCancelToken(cancelToken)
        });
    }
    queueAndWait(command, cancelToken) {
        const self = this;
        return new Promise((resolve, reject) => {
            const delegate = async (cancel) => {
                try {
                    const result = await self.handleCommand(command, cancel);
                    resolve(result);
                    return result;
                }
                catch (error) {
                    reject(error);
                    throw error;
                }
            };
            // queue the delegate for execution
            this.queueCommand(command, {
                delegate,
                cancelToken: this.commandCancelToken(cancelToken)
            });
        });
    }
    queueCommand(original, command) {
        this.currentQueueSize++;
        debug(`${this.debugName}: queueing command, queue size: ${this.currentQueueSize}`, original);
        if (!this.commandSubject) {
            throw new Error('Coding error: start has not called');
        }
        this.commandSubject.next(command);
    }
    commandCancelToken(cancelToken) {
        if (!cancelToken) {
            return this.cancelToken;
        }
        return new cancel_token_1.CompoundCancelToken(cancelToken, this.cancelToken);
    }
    async handleCommand(command, cancel) {
        debug('handleCommand', command);
        if (!command) {
            throw new Error('Coding error: empty command');
        }
        const result = await this.tryExecuteCommand(command, cancel);
        if (result.success) {
            return result;
        }
        if (result.error === 'canceled') {
            return result_1.err(canceledCommandError());
        }
        return await this.handleError(result.error, command, cancel);
    }
    async handleError(error, command, cancel) {
        debug('handleError', error, command, this.errorHandler);
        let errorHandler = this.errorHandler(error, command);
        debug('error handler result', errorHandler);
        while (true) {
            switch (errorHandler.type) {
                case 'skip':
                    this.notify({ type: 'skipped', command: command, error: error });
                    return result_1.err(commandError('skipped', error));
                case 'fatal':
                    this.notify({ type: 'fatal', command: command, error: error });
                    this.gotoFatalPhase();
                    this.errorReporting.sendFatalError('fatal error occurred while executing command', command, error);
                    return result_1.err(commandError('fatal', error));
                case 'retry': {
                    const retryResult = await this.retryCommand(command, error, cancel);
                    if (retryResult.success) {
                        return result_1.ok(retryResult.value);
                    }
                    if (retryResult.error === 'canceled') {
                        return result_1.err(canceledCommandError());
                    }
                    // new error handler
                    errorHandler = retryResult.error.errorHandler;
                    error = retryResult.error.error;
                    break;
                }
                case 'prepend': {
                    this.notify({
                        type: 'prepend-to-resolve-error',
                        command: command,
                        error: error,
                        commands: errorHandler.commands
                    });
                    for (let command of errorHandler.commands) {
                        const prependResult = await this.handleCommand(command, cancel);
                        if (!prependResult.success) {
                            if (prependResult.error.code === 'canceled' || prependResult.error.code === 'fatal') {
                                return prependResult;
                            }
                        }
                    }
                    return await this.handleCommand(command, cancel);
                }
                case 'custom-result':
                    this.notify({ type: 'executed-with-custom-result',
                        command: command,
                        error: error,
                        result: errorHandler.result });
                    return result_1.ok(errorHandler.result);
            }
        }
    }
    async retryCommand(command, error, cancel) {
        if (!this.retryStrategy) {
            throw new Error('Coding error: start has not called, we are in retryCommand');
        }
        const retry = this.retryStrategy.create();
        while (true) {
            const { retryCount } = await retry.next();
            debug('retrying', command);
            this.notify({ type: 'retry', command: command, error: error, retryCount: retryCount });
            const result = await this.tryExecuteCommand(command, cancel);
            if (result.success) {
                debug('retry succeeded', command);
                return result;
            }
            if (result.error === 'canceled') {
                debug('cancel while retrying');
                return result_1.err('canceled');
            }
            error = result.error;
            debug('retry error', error);
            const errorHandler = this.errorHandler(error, command);
            if (errorHandler.type !== 'retry') {
                debug('other error, stop retry', error);
                return result_1.err({
                    code: 'other-error',
                    errorHandler: errorHandler,
                    error: error
                });
            }
        }
    }
    async tryExecuteCommand(command, cancel) {
        if (cancel.cancel) {
            this.notify({ type: 'canceled', command: command });
            return result_1.err('canceled');
        }
        const result = await this.executeCommand(command, cancel);
        if (result.success) {
            this.notify({ type: 'executed', command: command, result: result.value });
        }
        else {
            if (result.error === 'canceled') {
                this.notify({ type: 'canceled', command: command });
            }
        }
        return result;
    }
    async executeCommand(command, cancel) {
        try {
            return await this.commandExecutor(command, cancel);
        }
        catch (ex) {
            return result_1.err(ex);
        }
    }
    notify(notification) {
        debug('notify', notification);
        this.notificationSubject.next(notification);
    }
}
exports.ErrorHandlingQueue = ErrorHandlingQueue;
//# sourceMappingURL=error-handling-queue.js.map